import { useState } from 'react';
import { Lock, ShieldCheck } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthErrorMessage } from '../../lib/authErrors';

interface ReAuthModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful re-authentication. */
  onSuccess: () => void;
  title?: string;
  description?: string;
}

/**
 * Prompts the signed-in user to confirm their password before a sensitive
 * action (delete account, change password/email). Calls `onSuccess` when the
 * re-authentication succeeds.
 */
export function ReAuthModal({
  open,
  onClose,
  onSuccess,
  title = 'Confirm it’s you',
  description = 'For your security, please re-enter your password to continue.',
}: ReAuthModalProps) {
  const { reauthenticate, user } = useAuth();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await reauthenticate(password);
      setPassword('');
      onSuccess();
    } catch (e) {
      setError(getAuthErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="space-y-5">
        <div className="flex items-start gap-3 text-sm text-[#666666]">
          <div className="w-10 h-10 rounded-full bg-[#F5F5F5] flex items-center justify-center shrink-0">
            <ShieldCheck size={20} />
          </div>
          <p>{description}</p>
        </div>

        {user?.email && (
          <p className="text-xs text-[#999999]">
            Signed in as <span className="font-semibold text-[#1A1A1A]">{user.email}</span>
          </p>
        )}

        <Input
          label="Password"
          type="password"
          icon={<Lock size={16} />}
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && password && submit()}
        />

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button fullWidth loading={busy} disabled={!password} onClick={submit}>
            Confirm
          </Button>
        </div>
      </div>
    </Modal>
  );
}
